import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Percent, GraduationCap, Plus, Trash2, Receipt, CalendarClock, HardHat } from "lucide-react";

// One page, per project: every price-changing input lives here so there are no surprise
// changes at the end. Seeds from company branding defaults; estimators override anything.
export default function CommercialTermsTab({ bid, computed, updateBid, readOnly }) {
  const [def, setDef] = useState({});
  useEffect(() => { api.get("/branding").then((r) => setDef(r.data || {})).catch(() => {}); }, []);
  const t = computed?.totals || {};
  const wb = bid.work_basis || {};
  const seed = (k, dflt) => (bid[k] ?? def[k] ?? dflt);
  const commit = (patch) => { if (!readOnly) updateBid(patch); };

  return (
    <div className="p-6 max-w-[1800px] w-full mx-auto space-y-5" data-testid="commercial-terms-tab">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Commercial Terms</h2>
        <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Set every price-changing input for <b>this project</b> up front — margins, terms, field basis and compliance — so nothing surprises you at rollup. Values seed from your defaults; change any of them here.</p>
      </div>

      {/* Price build-up — hits the total */}
      <Card className="p-5" data-testid="ct-price-buildup">
        <div className="flex items-center gap-1.5 mb-1"><Percent className="w-4 h-4 text-[#003E87]" /><h3 className="text-sm font-semibold text-slate-800">Price build-up</h3></div>
        <p className="text-xs text-slate-500 mb-4">Applied on top of direct cost to reach the contract price.</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          <PctRow label="Profit / fee" v={bid.margin_pct ?? bid.markup_pct} k="margin_pct" commit={commit} readOnly={readOnly}
            mode={bid.profit_mode || "margin"} onMode={(m) => commit({ profit_mode: m })} amount={t.markup_amount} testid="ct-profit" />
          <PctRow label="Contingency" v={bid.contingency_pct} k="contingency_pct" commit={commit} readOnly={readOnly}
            mode={bid.contingency_mode || "margin"} onMode={(m) => commit({ contingency_mode: m })} amount={t.contingency_amount} testid="ct-contingency" />
          <PctRow label="Bond (added to price)" v={bid.bond_pct} k="bond_pct" commit={commit} readOnly={readOnly} amount={t.bond_amount} testid="ct-bond" />
          <PctRow label="Warranty (added to price)" v={bid.warranty_pct} k="warranty_pct" commit={commit} readOnly={readOnly} amount={t.warranty_amount} testid="ct-warranty" />
        </div>
        <label className="flex items-center justify-between gap-2 py-2 mt-3 border-t border-slate-100 text-sm cursor-pointer" data-testid="ct-equipment-passthru">
          <span className="text-slate-600">Equipment is owner pass-through <span className="text-slate-400">(bill at cost, no markup)</span></span>
          <input type="checkbox" checked={!!bid.equipment_pass_through} disabled={readOnly} onChange={(e) => commit({ equipment_pass_through: e.target.checked })} data-testid="ct-equipment-passthru-input" />
        </label>
        <OtherFees bid={bid} totals={t} disabled={readOnly} commit={commit} />
        <div className="border-t border-slate-200 pt-3 mt-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-900">Contract price</span>
          <span className="font-mono text-lg font-bold text-slate-900" data-testid="ct-total">{money(t.total)}</span>
        </div>
      </Card>

      {/* Proposal & payment terms — schedule/figures, NOT added to the total */}
      <Card className="p-5" data-testid="ct-terms">
        <div className="flex items-center gap-1.5 mb-1"><CalendarClock className="w-4 h-4 text-[#003E87]" /><h3 className="text-sm font-semibold text-slate-800">Proposal &amp; payment terms</h3></div>
        <p className="text-xs text-slate-500 mb-4">These drive the proposal's payment schedule and deterministic figures (bond/warranty derivations, cash-flow curve, retainage) — they are <b>not</b> double-added to the price above.</p>
        <div className="grid sm:grid-cols-3 gap-x-6 gap-y-3">
          {[["mobilization_pct", "Mobilization billing %", 10], ["retainage_pct", "Retainage %", 10], ["payment_net_days", "Payment terms (net days)", 30],
            ["validity_days", "Proposal validity (days)", 30], ["bond_rate_pct", "Bond rate % (proposal only)", 1], ["warranty_rate_pct", "Warranty rate %/yr (proposal only)", 1.5],
            ["warranty_term_years", "Warranty term (yrs)", 2]].map(([k, lbl, dflt]) => (
            <NumField key={k} label={lbl} value={seed(k, dflt)} disabled={readOnly} testid={`ct-${k}`}
              onCommit={(v) => commit({ [k]: v })} />
          ))}
        </div>
      </Card>

      {/* Field basis */}
      <Card className="p-5" data-testid="ct-field-basis">
        <div className="flex items-center gap-1.5 mb-1"><HardHat className="w-4 h-4 text-[#003E87]" /><h3 className="text-sm font-semibold text-slate-800">Field basis</h3></div>
        <p className="text-xs text-slate-500 mb-4">Drives overtime (FLSA &gt;40h/wk) and how crew-days map to calendar days.</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          <NumField label="Work-week (days)" value={wb.days_per_week ?? def.work_week_days ?? 5} disabled={readOnly} testid="ct-work_week_days"
            onCommit={(v) => commit({ work_basis: { ...wb, days_per_week: v } })} />
          <NumField label="Hours / day" value={wb.hours_per_day ?? 10} disabled={readOnly} testid="ct-hours_per_day"
            onCommit={(v) => commit({ work_basis: { ...wb, hours_per_day: v } })} />
        </div>
      </Card>

      {/* Compliance */}
      <Card className="p-5" data-testid="ct-compliance">
        <div className="flex items-center gap-1.5 mb-1"><GraduationCap className="w-4 h-4 text-[#003E87]" /><h3 className="text-sm font-semibold text-slate-800">Workforce compliance</h3></div>
        <p className="text-xs text-slate-500 mb-4">Minimum apprentice ratio for this project. Crews you build must meet or exceed it — the crew popup won't let you drift below.</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          <NumField label="Apprenticeship % (minimum)" value={bid.apprenticeship_pct ?? 0} disabled={readOnly} testid="ct-apprenticeship_pct"
            onCommit={(v) => commit({ apprenticeship_pct: v })} />
        </div>
      </Card>

      {/* Labor burden */}
      <Card className="p-5" data-testid="ct-labor-burden">
        <div className="flex items-center gap-1.5 mb-1"><Receipt className="w-4 h-4 text-[#003E87]" /><h3 className="text-sm font-semibold text-slate-800">Labor burden</h3></div>
        <p className="text-xs text-slate-500 mb-4">Employer costs applied to every crew's <b>base wage</b> on this bid. Prevailing-wage fringes pass through untouched (no burden, no overtime).</p>
        <BurdenEditor value={bid.labor_burden ?? def.labor_burden} disabled={readOnly} commit={(lb) => commit({ labor_burden: lb })} />
      </Card>
    </div>
  );
}

function NumField({ label, value, disabled, onCommit, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input type="number" step="0.1" value={v ?? ""} disabled={disabled} data-testid={testid}
        onChange={(e) => setV(e.target.value === "" ? "" : Number(e.target.value))}
        onBlur={() => v !== value && onCommit(v === "" ? 0 : v)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className="mt-1 font-mono" />
    </div>
  );
}

function PctRow({ label, v, k, commit, readOnly, mode, onMode, amount, testid }) {
  const [val, setVal] = useState(v);
  useEffect(() => setVal(v), [v]);
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="flex-1">
        <Label className="text-xs text-slate-500">{label}</Label>
        <div className="flex items-center gap-1.5 mt-1">
          {onMode && (
            <select value={mode} disabled={readOnly} onChange={(e) => onMode(e.target.value)} data-testid={`${testid}-mode`}
              className="h-9 text-[11px] border border-slate-200 rounded px-1 bg-white text-slate-600">
              <option value="margin">margin</option><option value="markup">markup</option>
            </select>
          )}
          <div className="relative flex-1">
            <Input type="number" step="0.1" value={val ?? ""} disabled={readOnly} data-testid={testid}
              onChange={(e) => setVal(e.target.value === "" ? "" : Number(e.target.value))}
              onBlur={() => val !== v && commit({ [k]: val === "" ? 0 : val })}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="pr-6 text-right font-mono" />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
          </div>
        </div>
      </div>
      <div className="text-right pb-2 min-w-[84px]"><span className="font-mono text-xs text-slate-500" data-testid={`${testid}-amt`}>{amount ? money(amount) : "—"}</span></div>
    </div>
  );
}

function OtherFees({ bid, totals, disabled, commit }) {
  const fees = bid.other_fees || [];
  const set = (next) => commit({ other_fees: next });
  const upd = (i, key, v) => set(fees.map((f, j) => (j === i ? { ...f, [key]: v } : f)));
  const amts = totals?.other_fees || [];
  return (
    <div className="pt-3 mt-3 border-t border-slate-100" data-testid="ct-other-fees">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-600">Other fees</span>
        {!disabled && <button onClick={() => set([...fees, { name: "", pct: "", basis: "price" }])} data-testid="ct-add-fee" className="text-[11px] text-[#003E87] hover:underline">+ Add fee</button>}
      </div>
      {fees.map((f, i) => (
        <div key={i} className="flex items-center gap-1.5 mb-1" data-testid={`ct-fee-${i}`}>
          <input disabled={disabled} value={f.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Fee name" className="flex-1 min-w-0 text-xs border border-slate-200 rounded px-2 py-1.5 outline-none" />
          <input type="number" disabled={disabled} value={f.pct ?? ""} onChange={(e) => upd(i, "pct", e.target.value === "" ? "" : Number(e.target.value))} className="w-16 text-xs border border-slate-200 rounded px-2 py-1.5 text-right font-mono" placeholder="%" />
          <select disabled={disabled} value={f.basis || "price"} onChange={(e) => upd(i, "basis", e.target.value)} className="text-xs border border-slate-200 rounded px-1 py-1.5 bg-white"><option value="price">of price</option><option value="direct">of direct</option></select>
          <span className="font-mono text-[11px] text-slate-500 w-16 text-right">{money(amts[i]?.amount)}</span>
          {!disabled && <button onClick={() => set(fees.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-600 text-xs shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
      ))}
    </div>
  );
}

function BurdenEditor({ value, disabled, commit }) {
  const lb = value || {};
  let lines = lb.lines;
  if (!lines) {
    const items = lb.items || {};
    const LBL = { fica: "FICA / Medicare", futa: "FUTA", suta: "SUTA", workers_comp: "Workers' Comp", general_liability: "General Liability" };
    lines = Object.keys(items).length ? Object.entries(items).map(([k, v]) => ({ name: LBL[k] || k, pct: Number(v) || 0 })) : [];
  }
  const total = lines.reduce((s, x) => s + (Number(x.pct) || 0), 0);
  const save = (next) => commit({ mode: "itemized", lines: next });
  const setLine = (i, f, v) => save(lines.map((x, j) => (j === i ? { ...x, [f]: f === "pct" ? (v === "" ? "" : Number(v)) : v } : x)));
  return (
    <>
      <div className="space-y-2">
        {lines.length === 0 && <div className="text-xs text-slate-400 italic">No burden lines yet — add the employer costs you carry (FICA, SUTA, workers' comp, or anything custom).</div>}
        {lines.map((x, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`ct-burden-line-${i}`}>
            <input value={x.name ?? ""} disabled={disabled} onChange={(e) => setLine(i, "name", e.target.value)} placeholder="Burden name (e.g. SUTA)" data-testid={`ct-burden-name-${i}`} className="flex-1 text-sm border border-slate-200 rounded px-2 py-1.5" />
            <div className="relative w-28">
              <input type="number" step="0.01" value={x.pct ?? ""} disabled={disabled} onChange={(e) => setLine(i, "pct", e.target.value)} placeholder="0.00" data-testid={`ct-burden-pct-${i}`} className="w-full pr-6 text-right font-mono text-sm border border-slate-200 rounded px-2 py-1.5" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
            </div>
            {!disabled && <button onClick={() => save(lines.filter((_, j) => j !== i))} data-testid={`ct-burden-remove-${i}`} className="text-slate-300 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      {!disabled && <button onClick={() => save([...lines, { name: "", pct: "" }])} data-testid="ct-burden-add" className="text-xs text-[#003E87] hover:underline flex items-center gap-1 mt-2"><Plus className="w-3.5 h-3.5" /> Add burden line</button>}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2 text-sm">
        <span className="text-slate-500">Total burden on base wage</span>
        <span className="font-mono font-bold text-slate-900" data-testid="ct-burden-total">{total.toFixed(2)}%</span>
      </div>
    </>
  );
}
